// todo: get the server url based on the environment
import { Redemption, RedemptionResult } from '@/api/models/redemption';
import APIHelpers from '../apiHelpers';
import CodeOption, { CodeOptionMap } from '../models/codeOption';
import Question from '../models/question';
import { IToken, Token } from '../models/token';
import { CampaignShopifyProductDetails, CampaignShopifyRule } from '../shopify/types';

export enum CampaignAction {
  QUESTION = 1,
  SHOPIFY_URL = 2,
  SHOPIFY_DISCOUNT = 3,
  SHOPIFY_DELAYED = 4,
  CODE = 5,
  CODE_WITH_QUESTIONS = 6
}

enum CampaignType {
  ERC721 = 1,
  ERC1155 = 2
}
export interface Campaign {
  id?: number;
  cognitoUserId: string;
  name: string;
  type: CampaignType;
  action: CampaignAction;
  snapshot: boolean;
  startDate: Date;
  endDate: Date;
  webhookUrl: string | null;
  active: boolean | null;
  maxRedemptions: number | null;
  redemptionsPerToken: number;
  redemptionsPerAddress: number | null;
}

export interface CampaignWithShopifyProduct {
  campaign: Campaign;
  productId: number;
  shop: string;
  productData: any;
  discountPercent: number | null;
  discountAmount: number | null;
  limit: number;
  total: number | null;
  consumed: number;
  rules: CampaignShopifyRule[];
}

const REDEEM_BACKEND = 'https://redeem.api.manifoldxyz.dev';

class RedeemServerClient {
  apiHelpers: APIHelpers;

  constructor() {
    this.apiHelpers = new APIHelpers(REDEEM_BACKEND);
  }

  checkTokenHasBeenUsed = async (
    campaignId: number,
    network: number,
    tokenAddress: string,
    tokenId: string
  ): Promise<boolean> => {
    const hasBeenUsed = await this.apiHelpers.fetch(
      `/campaign/${campaignId}/check/${network}/${tokenAddress}/${tokenId}`
    );
    return hasBeenUsed.redeemed;
  };

  getCart = async (oauthToken: string, shop: string): Promise<any> => {
    const shopifyCart = await this.apiHelpers.fetch(`/shopify/${shop}/cart`, {}, oauthToken);
    return shopifyCart;
  };

  createOrUpdateCart = async (oauthToken: string, shop: string, cart: any): Promise<any> => {
    const body = cart;
    const shopifyCart = await this.apiHelpers.post(
      `/shopify/${shop}/cart`,
      {
        body
      },
      oauthToken
    );
    return shopifyCart;
  };

  checkoutRedeemCart = async (oauthToken: string, shop: string, cart: any): Promise<any> => {
    const body = {
      cart: cart
    };
    const shopifyCart = await this.apiHelpers.post(
      `/shopify/${shop}/checkout`,
      {
        body
      },
      oauthToken
    );
    return shopifyCart;
  };

  getCampaignForProduct = async (
    shop: string,
    productId: string
  ): Promise<Partial<CampaignWithShopifyProduct>> => {
    // For when testing locally for shopify
    if (window.location.hostname === 'localhost') {
      return {
        // @ts-ignore
        id: 940062587 // Change ID to whatever works for what you are testing
      };
    }
    try {
      const campaign = await this.apiHelpers.fetch(
        `/shopify/${shop}/product/${productId}/campaign`,
        {}
      );
      return campaign;
    } catch (e) {
      // @ts-ignore
      if (e.status == 404) {
        return {};
      }
      throw e;
    }
  };

  getProductIsUnrestricted = async (shop: string, productId: string): Promise<boolean> => {
    try {
      const unrestrictedRes = await this.apiHelpers.fetch(
        `/shopify/${shop}/product/${productId}/unrestricted`,
        {}
      );
      return unrestrictedRes.unrestricted;
    } catch (e) {
      // @ts-ignore
      if (e.status == 404) {
        return true; // if 404, then it must not be restricted...
      }
      throw e;
    }
  };

  getEligibleAwardsForProduct = async (
    oauthToken: string,
    shop: string,
    productId: string
  ): Promise<Partial<Campaign>[]> => {
    let awards: any[] = [];
    try {
      awards = await this.apiHelpers.fetch(
        `/shopify/${shop}/product/${productId}/rewards?eip5131=true&eip5639=true`,
        {},
        oauthToken
      );
    } catch (e) {
      // @ts-ignore
      if (e.status == 404) {
        return [];
      }
    }
    return awards;
  };

  // api to get campaign
  getCampaign = async (campaignId: number): Promise<Partial<Campaign>> => {
    const campaign = await this.apiHelpers.fetch(`/campaign/${campaignId}`);
    return campaign;
  };

  // api fetches all the tokens pariticipating in a campaign for the connected user
  getEligibleTokens = async (oauthToken: string, campaignId: number): Promise<Array<IToken>> => {
    const tokens = await this.apiHelpers.fetch(
      `/campaign/${campaignId}/token?eip5131=true&eip5639=true`,
      {},
      oauthToken
    );
    const tokenData: Token[] = tokens.map((x: any) => {
      return Token.fromJson(x);
    });
    return tokenData;
  };

  // Gets all the questions a user is expected to fill out for a campaign
  getCampaignQuestions = async (campaignId: number) => {
    const questions = await this.apiHelpers.fetch(`/campaign/${campaignId}/questions`);
    const questionData = questions.map((x: any) => {
      return Question.fromJson(x);
    });
    return questionData;
  };

  // Gets the options for a QR code type campaign
  getCampaignCodeOptions = async (campaignId: number): Promise<CodeOptionMap> => {
    const options = await this.apiHelpers.fetch(`/campaign/${campaignId}/code/options`);
    return options.reduce((aggregate: CodeOptionMap, option: CodeOption) => {
      aggregate[option.id] = CodeOption.fromJson(option);
      return aggregate;
    }, {});
  };

  getQuestionResults = async (campaignId: number, questionId: number) => {
    const questionsResults = await this.apiHelpers.fetch(
      `/campaign/${campaignId}/questions/${questionId}/results`
    );
    return questionsResults;
  };

  getCampaignShopifyProducts = async (
    campaignId: number
  ): Promise<CampaignShopifyProductDetails[]> => {
    const shopifyProducts = await this.apiHelpers.fetch(`/campaign/${campaignId}/shopify/products`);
    return shopifyProducts;
  };

  // api fetches all the redemptions availed by the connected user
  getCampaignShopifyDiscountDescription = async (
    oauthToken: string,
    campaignId: number
  ): Promise<any> => {
    return (
      await this.apiHelpers.fetch(
        `/campaign/${campaignId}/shopify/discount/description`,
        {},
        oauthToken
      )
    ).description;
  };

  // api fetches all the redemptions availed by the connected user
  getRedemptions = async (
    oauthToken: string,
    campaignId: number
  ): Promise<Array<RedemptionResult>> => {
    const redemptions = await this.apiHelpers.fetch(
      `/campaign/${campaignId}/redemption`,
      {},
      oauthToken
    );
    const redemptionData = redemptions.map((x: any) => {
      return RedemptionResult.fromJson(x);
    });
    return redemptionData;
  };

  getRedemptionsByOthers = async (
    oauthToken: string,
    campaignId: number
  ): Promise<Array<RedemptionResult>> => {
    const redemptions = await this.apiHelpers.fetch(
      `/campaign/${campaignId}/redemption/other`,
      {},
      oauthToken
    );
    const redemptionData = redemptions.map((x: any) => {
      return RedemptionResult.fromJson(x);
    });

    return redemptionData;
  };

  getRedemptionsForProduct = async (
    oauthToken: string,
    shop: string,
    productId: string
  ): Promise<Array<RedemptionResult>> => {
    const campaign = await this.getCampaignForProduct(shop, productId);

    let redemptionData: RedemptionResult[] = [];
    const redemptions = await this.apiHelpers.fetch(
      `/campaign/${campaign.campaign?.id}/redemption`,
      {},
      oauthToken
    );
    redemptionData = redemptionData.concat(
      redemptions.map((x: any) => {
        return RedemptionResult.fromJson(x);
      })
    );

    return redemptionData;
  };

  getShopifyQueue = async (oauthToken: string, campaignId: number): Promise<void> => {
    const queue = await this.apiHelpers.fetch(
      `/campaign/${campaignId}/shopify/queue`,
      {},
      oauthToken
    );
    return queue;
  };

  // api to submit redemption for the connected user
  submitRedemption = async (
    oauthToken: string,
    campaignId: number,
    redemption: Redemption,
    redemptionId?: string | number
  ) => {
    let body = {
      network: redemption.network,
      tokenAddress: redemption.tokenAddress,
      tokenId: redemption.tokenId,
      submissions: redemption.submissions,
      eip5131Address: redemption.eip5131Address ? redemption.eip5131Address : '',
      eip5639Address: redemption.eip5639Address ? redemption.eip5639Address : ''
    };

    if (redemptionId) {
      body = {
        ...body,
        // @ts-ignore
        redemptionId
      };
    }

    return await this.apiHelpers.post(
      `/campaign/${campaignId}/redemption`,
      { body: body },
      oauthToken
    );
  };

  submitRedemptionDiscount = async (
    oauthToken: string,
    campaignId: number,
    redemption: Redemption
  ) => {
    const body = {
      network: redemption.network,
      tokenAddress: redemption.tokenAddress,
      tokenId: redemption.tokenId
    };

    return await this.apiHelpers.post(
      `/campaign/${campaignId}/redemption`,
      { body: body },
      oauthToken
    );
  };

  async clearCart(oauthToken: string, shop: string) {
    return await this.apiHelpers.post(`/shopify/${shop}/cart/clear`, {}, oauthToken);
  }
}

export default RedeemServerClient;
